import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { EmployeeService } from '../../services/employee.service';
import { LogService } from '../../services/log.service';

// Importaciones de interfaces.
import { IEmployee } from '../../interfaces/employee.interface';

// Importaciones de modelos.
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la eliminación del empleado.
 *
 * Este componente proporciona una interfaz para confirmar y realizar la eliminación del empleado.
 * Se muestra como un cuadro de diálogo, permitiendo al usuario confirmar la acción antes de proceder con la eliminación.
 */
@Component({
  selector: 'app-employee-delete-dialog',
  templateUrl: './employee-delete-dialog.component.html',
  standalone: false,
})
export class EmployeeDeleteDialogComponent {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de eliminación del empleado.
   */
  readonly dialogRef: MatDialogRef<EmployeeDeleteDialogComponent> = inject(
    MatDialogRef<EmployeeDeleteDialogComponent>
  );

  /**
   * Datos del empleado proporcionados al diálogo.
   *
   * Se inyectan desde MAT_DIALOG_DATA y son de solo lectura.
   */
  private readonly employee: IEmployee = inject<IEmployee>(MAT_DIALOG_DATA);

  /**
   * Indica si la operación de eliminado está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se elimina el empleado.
   */
  loading: boolean = false;

  /**
   * Lista de errores.
   *
   * Contiene los mensajes de error generados si la operación de eliminado falla.
   */
  errors: string[] = [];

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param employeeService - Servicio para gestionar la información de los empleados.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private employeeService: EmployeeService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Confirma y elimina el empleado actual.
   *
   * Muestra un diálogo de confirmación antes de proceder con la eliminación.
   * Si la eliminación es exitosa, cierra el diálogo.
   */
  async delete(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      const employeeResponse: string = await this.employeeService.delete(
        this.employee
      );

      // Añadir un registro de eliminación de empleado.
      try {
        await this.logService.create(
          new Log({
            description: `Ha eliminado el empleado '${this.employee.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de eliminación de empleado.'
          );
        }
      }

      this.snackBar.open(employeeResponse, '', { duration: 5000 });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
